import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { APP_VERSION } from '@/lib/version';

/**
 * Public (unauthenticated) page shell — same visual frame as the login page:
 * tonal background, oversized wordmark, card with wordmark + env pill,
 * footer meta row. Used by forgot/reset-password pages.
 */
export function AuthPageShell({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  return (
    <div className="relative grid min-h-screen place-items-center overflow-hidden bg-background p-4">
      {/* Quiet tonal field */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            'radial-gradient(120% 90% at 50% 0%, hsl(var(--accent) / 0.55) 0%, hsl(var(--background)) 62%)',
        }}
      />
      {/* Oversized faint wordmark */}
      <span
        aria-hidden="true"
        className="pointer-events-none absolute -bottom-[0.16em] left-1/2 -translate-x-1/2 select-none whitespace-nowrap font-serif italic leading-none text-foreground/[0.025]"
        style={{ fontSize: 'clamp(140px, 26vw, 420px)' }}
      >
        License
      </span>

      <div className="relative w-full max-w-[400px] motion-safe:duration-500 motion-safe:animate-in motion-safe:fade-in">
        <div className="rounded-[10px] border bg-card px-6 py-8 shadow-[0_1px_0_0_hsl(var(--foreground)/0.03),0_16px_48px_-16px_hsl(33_18%_4%/0.55)] sm:px-9 sm:py-10">
          {/* Wordmark + env */}
          <div className="flex items-center justify-between">
            <span className="flex items-center gap-2.5">
              <span className="h-2.5 w-2.5 rounded-[3px] bg-primary" aria-hidden="true" />
              <span className="text-[19px] font-semibold tracking-tight text-foreground">
                License
              </span>
            </span>
            <span className="inline-flex items-center gap-1.5 rounded-full border border-status-active/25 bg-status-active/10 px-2.5 py-1 text-[12px] font-medium text-foreground">
              <span className="h-1.5 w-1.5 rounded-full bg-status-active" aria-hidden="true" />
              {t('shell.envBadge')}
            </span>
          </div>

          {children}

          {/* Footer meta row */}
          <div className="mt-9 flex items-center justify-between border-t pt-4 font-mono text-xs text-faint">
            <span>{APP_VERSION}</span>
            <span>{t('login.footerMeta')}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
